import { startOfDay } from "date-fns";
import { useGetIdentity, useGetList } from "ra-core";
import { useMemo } from "react";

import type { DealMilestone, TeamTask } from "@/components/fretum/types";

import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Deal, Task } from "../../types";
import {
  buildAttentionInbox,
  collectBuckets,
  dealsWithoutNextStep,
  homeCounters,
} from "./homeModel";
import type { HubEvent, Invoice } from "./homeTypes";

const LIST = { page: 1, perPage: 1000 } as const;
/** Hub tables may be missing (FakeRest tests): fail fast, render empty. */
const HUB_QUERY = { retry: false } as const;

/**
 * Shared list queries of the home (react-query dedupes identical calls, so
 * the header, view A and view B all reuse them). One query per resource, no
 * per-row fetches.
 */
export const useOpenDeals = () =>
  useGetList<Deal>("deals", {
    pagination: LIST,
    sort: { field: "id", order: "ASC" },
    filter: { "archived_at@is": null },
  });

export const useHomeInvoices = () =>
  useGetList<Invoice>(
    "invoices",
    {
      pagination: LIST,
      sort: { field: "issue_date", order: "DESC" },
      filter: { "status@neq": "draft" },
    },
    HUB_QUERY,
  );

export const useOpenMilestones = () =>
  useGetList<DealMilestone>(
    "deal_milestones",
    {
      pagination: LIST,
      sort: { field: "due_date", order: "ASC" },
      filter: { "status@neq": "done" },
    },
    HUB_QUERY,
  );

/**
 * Everything view A needs: the attention inbox, its counters and the
 * receivables summary. Overdue items come from unfiltered "open" queries,
 * so nothing old is cut off.
 */
export const useHomeToday = () => {
  const { identity } = useGetIdentity();
  const { dealPipelineStatuses } = useConfigurationContext();
  const todayStart = useMemo(() => startOfDay(new Date()).toISOString(), []);

  const tasks = useGetList<Task>("tasks", {
    pagination: LIST,
    sort: { field: "due_date", order: "ASC" },
    filter: { "done_date@is": null },
  });
  const teamTasks = useGetList<TeamTask>(
    "team_tasks",
    {
      pagination: LIST,
      sort: { field: "due_date", order: "ASC" },
      filter: { "status@neq": "done" },
    },
    HUB_QUERY,
  );
  const milestones = useOpenMilestones();
  const events = useGetList<HubEvent>(
    "events",
    {
      pagination: LIST,
      sort: { field: "start_at", order: "ASC" },
      filter: { "start_at@gte": todayStart },
    },
    HUB_QUERY,
  );
  const deals = useOpenDeals();
  const invoices = useHomeInvoices();

  const result = useMemo(() => {
    const now = new Date();
    const me = identity?.id;
    const isMine = (id: unknown) =>
      me != null && id != null && String(id) === String(me);
    const allTasks = tasks.data ?? [];
    const allTeamTasks = teamTasks.data ?? [];
    const noNextStep = dealsWithoutNextStep(
      deals.data ?? [],
      {
        tasks: allTasks,
        teamTasks: allTeamTasks,
        events: events.data ?? [],
        milestones: milestones.data ?? [],
      },
      dealPipelineStatuses,
      now,
    );
    const inbox = buildAttentionInbox(
      {
        tasks: allTasks.filter((task) => isMine(task.sales_id)),
        teamTasks: allTeamTasks.filter((task) => isMine(task.assignee_id)),
        milestones: milestones.data ?? [],
        events: events.data ?? [],
        noNextStep,
        invoices: invoices.data ?? [],
      },
      now,
    );
    return {
      inbox,
      counters: homeCounters(inbox),
      collect: collectBuckets(invoices.data ?? [], now),
    };
  }, [
    identity?.id,
    tasks.data,
    teamTasks.data,
    milestones.data,
    events.data,
    deals.data,
    invoices.data,
    dealPipelineStatuses,
  ]);

  return {
    ...result,
    deals: deals.data ?? [],
    /** The core lists are in; hub lists may still be loading or missing. */
    isPending: tasks.isPending || deals.isPending || identity?.id == null,
    hasInvoices: !invoices.isError,
  };
};
